/**
 * Date formatting — SIH v1.1 look & feel (§3.3 Dates).
 *
 * Two distinct, presentation-only formatters. Both are pure functions over
 * the raw source string — never `new Date(...)` on raw input (the source's
 * compact `yyyyMMddHHmmss` shape would parse as a millisecond timestamp and
 * drift into 1970). Both honour the contract: pass through unrecognised
 * input verbatim (ADR-000 F2: the SPA renders the source, never invents
 * semantics); null in → null out.
 *
 *   formatTimestamp(raw, now) → { absolute, relative }
 *     - absolute : `YYYY-MM-DD HH:mm`     (presentation-grade, minute precision)
 *     - relative : `3 h ago` / `5 min ago` / `just now` (only when within 7 days)
 *
 *   formatFreshness(raw)       → `YYYY-MM-DD HH:mm:ss` (audit-grade, existing v1 contract)
 *
 * The existing `formatFreshness` was promoted out of `FreshnessIndicator.tsx`
 * to keep the file purely presentational; the formatters live here so all
 * date shaping has one source of truth.
 *
 * LAYER LAW: this is the innermost layer. No imports from `@components`,
 * `@stores`, `@pages`, `@api`, or any browser/DOM-only API.
 */

const COMPACT_RE = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/

/** Output of `formatTimestamp` — the table/header pair render (proposal §3.3). */
export interface FormattedTimestamp {
  /** Absolute display: `YYYY-MM-DD HH:mm`, or `null` when the raw input is unrecognised / null. */
  readonly absolute: string | null
  /** Relative age: `just now` / `3 min ago` / `5 h ago` / `2 d ago`, or `null` outside the 7-day window or when unrecognised. */
  readonly relative: string | null
}

/**
 * Format a source timestamp for the table cells (SUBMITTED, CREATED).
 * `now` is injected so the formatter is pure and trivially testable.
 *
 * @param raw  source string — `yyyyMMddHHmmss`, ISO-8601, or unrecognised (pass-through)
 * @param now reference clock for relative-age computation
 */
export function formatTimestamp(raw: string | null, now: Date): FormattedTimestamp {
  if (raw === null) return { absolute: null, relative: null }
  const compact = COMPACT_RE.exec(raw)
  if (compact !== null) {
    // Anchored full-match — all 6 groups required — non-null assertions safe.
    const [y, mo, d, h, mi, s] = captureAllSix(compact)
    // Seconds feed the relative-age computation (accurate); the absolute
    // display is minute-precision per proposal §3.3.
    const absolute = `${y}-${mo}-${d} ${h}:${mi}`
    return { absolute, relative: relativeAge(parseAsUtc(+y, +mo, +d, +h, +mi, +s), now) }
  }
  const iso = ISO_RE.exec(raw)
  if (iso !== null) {
    const [y, mo, d, h, mi] = captureAllSix(iso)
    // Group 6 is optional — undefined means the source omitted seconds.
    // Naive ISO without timezone is treated as UTC (defensible per ADR-000
    // F2: the SPA renders the source, never invents timezone semantics).
    const secRaw = iso[6]
    const s = secRaw !== undefined ? +secRaw : 0
    const ms = Date.UTC(+y, +mo - 1, +d, +h, +mi, s)
    const minuteForm = `${y}-${mo}-${d} ${h}:${mi}`
    return { absolute: minuteForm, relative: relativeAge(new Date(ms), now) }
  }
  return { absolute: raw, relative: null }
}

/**
 * Format the freshness timestamp for the header (existing v1 contract).
 * Compact `yyyyMMddHHmmss` → `yyyy-MM-dd HH:mm:ss`; ISO → truncated to seconds
 * without `T`/`Z`; other shapes pass through verbatim.
 */
export function formatFreshness(raw: string | null): string | null {
  if (raw === null) return null
  const compact = COMPACT_RE.exec(raw)
  if (compact !== null) {
    const [y, mo, d, h, mi, s] = captureAllSix(compact)
    return `${y}-${mo}-${d} ${h}:${mi}:${s}`
  }
  const iso = ISO_RE.exec(raw)
  if (iso !== null) {
    const [y, mo, d, h, mi] = captureAllSix(iso)
    // Group 6 is optional — undefined means the source omitted seconds.
    const secRaw = iso[6]
    const sec = secRaw !== undefined ? secRaw : '00'
    return `${y}-${mo}-${d} ${h}:${mi}:${sec}`
  }
  return raw
}

// ---------- internal helpers ----------

/**
 * Pull the first six capture groups out of a regex match. Anchored full-match
 * regexes with required groups guarantee every group is defined on success;
 * non-null assertions on `g` reflect the runtime guarantee. The typed
 * tuple keeps call sites readable.
 */
function captureAllSix(match: RegExpExecArray): readonly [string, string, string, string, string, string] {
  return [match[1]!, match[2]!, match[3]!, match[4]!, match[5]!, match[6]!]
}

function parseAsUtc(y: number, mo: number, d: number, h: number, mi: number, s: number): Date {
  return new Date(Date.UTC(y, mo - 1, d, h, mi, s))
}

const MS_MIN = 60_000
const MS_HOUR = 60 * MS_MIN
const MS_DAY = 24 * MS_HOUR
const RELATIVE_WINDOW_MS = 7 * MS_DAY

/**
 * Compute the relative-age label for the absolute instant vs `now`.
 * - `just now` for under 1 minute (handles tiny clock skew + same-second renders)
 * - `N min ago` for under 1 hour
 * - `N h ago` for under 24 hours
 * - `N d ago` for under the 7-day window
 * - `null` beyond — the table falls back to the absolute display only
 *
 * Negative deltas (clock skew, future-dated timestamps) clamp to `just now`
 * rather than rendering `-3 h ago` — the SPA renders the source's honest
 * age, not a paranoid clock-delta.
 */
function relativeAge(instant: Date, now: Date): string | null {
  const delta = now.getTime() - instant.getTime()
  if (delta < MS_MIN) return 'just now'
  if (delta >= RELATIVE_WINDOW_MS) return null
  if (delta < MS_HOUR) return `${Math.floor(delta / MS_MIN)} min ago`
  if (delta < MS_DAY) return `${Math.floor(delta / MS_HOUR)} h ago`
  return `${Math.floor(delta / MS_DAY)} d ago`
}